/**
 * salesAssetsApi — activos comerciales (el dossier real), desde el admin.
 *
 * Envoltorio fino sobre /api/admin/sales-assets. Cada helper devuelve el
 * cuerpo ya parseado y deja pasar el error tal cual, para que la pantalla lo
 * pinte con `formatError(e)` como el resto del admin.
 *
 * La subida va por TROZOS de bytes crudos, no en base64 dentro de un JSON:
 * el dossier pesa ~24 MB y el límite de cuerpo de nginx en producción no se
 * puede comprobar desde el repo. El navegador calcula antes el sha256 del
 * fichero (crypto.subtle) y lo declara al abrir la subida; el servidor
 * recalcula la huella al cerrar y rechaza cualquier discrepancia, así que un
 * trozo corrompido por el camino nunca llega al catálogo.
 *
 * Los trozos son idempotentes por offset: reintentar uno que ya llegó no lo
 * duplica, y si el servidor espera otro offset (409) dice cuál, y se retoma
 * desde ahí. Por eso este bucle puede reintentar sin miedo.
 */
import { api, API } from "@/lib/api";

const BASE = "/admin/sales-assets";

// Cuántas veces seguidas se acepta que el servidor pida otro offset, o que
// un trozo se pierda por la red, antes de rendirse y enseñar el error. Sin
// tope, un servidor que contestara siempre lo mismo dejaría el bucle vivo.
const MAX_RESYNCS = 5;
const MAX_NETWORK_RETRIES = 3;

// Espera antes de cada reintento, creciendo: un proxy que devuelve 502
// mientras el backend reinicia no se arregla en 0 ms, y reintentar al
// instante gasta los tres intentos en el mismo corte.
const RETRY_DELAYS_MS = [500, 1000, 2000];
const retryDelay = (n) => RETRY_DELAYS_MS[Math.min(n, RETRY_DELAYS_MS.length) - 1];
const defaultSleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// 502/503/504 son el proxy o el backend de paso, no un «no» del servidor:
// se reintentan como un corte de red, dentro del MISMO presupuesto.
const TRANSIENT_STATUSES = new Set([502, 503, 504]);

const enc = (id) => encodeURIComponent(String(id));

export async function listSalesAssets() {
  const { data } = await api.get(BASE);
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    kinds: Array.isArray(data?.kinds) ? data.kinds : [],
    languages: Array.isArray(data?.languages) ? data.languages : [],
    chunkSize: Number(data?.chunk_size) || 0,
    maxBytes: Number(data?.max_bytes) || 0,
  };
}

/** Diagnóstico del directorio: escribible (probado), espacio, huérfanos. */
export async function getSalesAssetsStorage() {
  const { data } = await api.get(`${BASE}/storage`);
  return data;
}

export async function activateSalesAsset(assetId) {
  const { data } = await api.post(`${BASE}/${enc(assetId)}/activate`);
  return data;
}

export async function deactivateSalesAsset(assetId) {
  const { data } = await api.post(`${BASE}/${enc(assetId)}/deactivate`);
  return data;
}

/** check_deliverable + la descarga anónima real por la URL pública. */
export async function verifySalesAsset(assetId) {
  const { data } = await api.post(`${BASE}/${enc(assetId)}/verify`);
  return data;
}

/** Descarga con la sesión del admin (cookie), como los ficheros del CRM. */
export function salesAssetDownloadUrl(assetId) {
  return `${API}${BASE}/${enc(assetId)}/download`;
}

function readAsArrayBuffer(blob) {
  if (typeof blob?.arrayBuffer === "function") return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("No se pudo leer el fichero."));
    reader.readAsArrayBuffer(blob);
  });
}

/** sha256 del fichero en hexadecimal, calculado en el navegador. */
export async function sha256Hex(blob) {
  const subtle = globalThis.crypto && globalThis.crypto.subtle;
  if (!subtle) {
    // Sin huella no hay subida: el servidor la exige para poder rechazar un
    // fichero que llegue distinto de como salió.
    throw new Error("Este navegador no puede calcular la huella del fichero.");
  }
  // Normaliza a un TypedArray del realm actual antes de pasarlo a
  // WebCrypto. FileReader puede devolver un ArrayBuffer de otro realm
  // (por ejemplo jsdom/iframes); algunas implementaciones de SubtleCrypto
  // lo rechazan aunque el contenido sea válido.
  const raw = await readAsArrayBuffer(blob);
  const bytes = Uint8Array.from(new Uint8Array(raw));
  const digest = await subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

const isNetworkError = (e) => !!e && !e.response;
const isRetryable = (e) => isNetworkError(e) || TRANSIENT_STATUSES.has(e?.response?.status);

/**
 * Sube un PDF como versión nueva (INACTIVA) y devuelve la respuesta del
 * cierre: { ok, created, asset }.
 *
 * `onProgress({ phase, sent, total })` con phase ∈ hashing | uploading |
 * completing. `sleep(ms)` es la espera entre reintentos; se inyecta solo
 * para que los tests no esperen de verdad.
 */
export async function uploadSalesAsset({
  file, kind, language, onProgress, sleep = defaultSleep,
} = {}) {
  const report = typeof onProgress === "function" ? onProgress : () => {};
  const total = file.size;
  report({ phase: "hashing", sent: 0, total });
  const sha256 = await sha256Hex(file);

  const { data: opened } = await api.post(`${BASE}/uploads`, {
    kind, language, filename: file.name, size_bytes: total, sha256,
  });
  const uploadId = opened.upload_id;
  const chunkSize = Number(opened.chunk_size);
  if (!uploadId || !(chunkSize > 0)) throw new Error("Respuesta inesperada del servidor.");

  let offset = 0;
  let resyncs = 0;
  let retries = 0;
  report({ phase: "uploading", sent: 0, total });
  while (offset < total) {
    const piece = file.slice(offset, offset + chunkSize);
    let data;
    try {
      ({ data } = await api.put(
        `${BASE}/uploads/${enc(uploadId)}/chunks`,
        piece,
        { params: { offset }, headers: { "Content-Type": "application/octet-stream" } },
      ));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "offset_mismatch"
          && Number.isInteger(detail.expected_offset) && resyncs < MAX_RESYNCS) {
        resyncs += 1;
        offset = detail.expected_offset;
        continue;
      }
      if (isRetryable(e) && retries < MAX_NETWORK_RETRIES) {
        retries += 1;
        await sleep(retryDelay(retries));
        continue;
      }
      throw e;
    }
    const received = Number(data?.received);
    // Lo recibido siempre avanza: si no, algo no cuadra y seguir sería un
    // bucle infinito contra el servidor.
    if (!Number.isInteger(received) || received <= offset) {
      throw new Error("Respuesta inesperada del servidor.");
    }
    offset = received;
    resyncs = 0;
    retries = 0;
    report({ phase: "uploading", sent: offset, total });
  }

  report({ phase: "completing", sent: total, total });
  // Cerrar también es idempotente: si la respuesta se pierde, repetirlo
  // devuelve la misma versión en vez de crear otra.
  for (let intento = 0; ; intento += 1) {
    try {
      const { data } = await api.post(`${BASE}/uploads/${enc(uploadId)}/complete`);
      return data;
    } catch (e) {
      if (!isRetryable(e) || intento >= MAX_NETWORK_RETRIES) throw e;
      await sleep(retryDelay(intento + 1));
    }
  }
}
